import React, { useState, useEffect, useRef } from 'react';
import { tasksApi, roomsApi } from '../../services/api';
import { useConfirm, useNotify } from '../../context/ConfirmContext';

const TASK_CATEGORIES = [
  { id: 'Wake-up Call', label: '⏰ Wake-up Call' },
  { id: 'Parcel / Delivery', label: '📦 Parcel / Order Delivery' },
  { id: 'Cleaning', label: '🧹 Room Cleaning' },
  { id: 'Appointment', label: '📅 Guest Appointment' },
  { id: 'Maintenance', label: '🛠️ Maintenance / Repair' },
  { id: 'General', label: '📝 General Follow-up' },
  { id: 'Others', label: '✏️ Others (Custom)' },
];

function formatTime12(time24) {
  if (!time24) return '';
  if (time24.includes('AM') || time24.includes('PM')) return time24;
  const parts = time24.split(':');
  if (parts.length < 2) return time24;
  let h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return time24;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  const mStr = m < 10 ? '0' + m : m;
  return `${h}:${mStr} ${ampm}`;
}

function getOffsetTimeString(mins) {
  const d = new Date(Date.now() + mins * 60 * 1000);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function playTaskSound(tone = 'soft') {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    if (tone === 'beep') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } else if (tone === 'bell') {
      [440, 880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        gain.gain.setValueAtTime(0.1 / (i + 1), ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.8);
      });
    } else {
      // Default: 'soft' Dual-tone Chime
      const now = ctx.currentTime;
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523.25, now);
      gain1.gain.setValueAtTime(0.12, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.4);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(659.25, now + 0.15);
      gain2.gain.setValueAtTime(0.12, now + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.15);
      osc2.stop(now + 0.6);
    }
  } catch (err) {
    console.error('Audio play error:', err);
  }
}

export default function GuestTasksWidget({ hotel, user, rooms: propsRooms }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [tasks, setTasks] = useState([]);
  const [rooms, setRooms] = useState(propsRooms || []);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending'); // 'pending', 'completed', 'all'
  const [modalOpen, setModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reminderPopupOpen, setReminderPopupOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    room_number: '',
    category: 'Wake-up Call',
    custom_category: '',
    title: '',
    notes: '',
    due_time: '',
    target_shift: 'Any'
  });

  // Alarm preferences state
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('staygroup_task_sound_enabled') !== 'false';
  });
  const [alarmTone, setAlarmTone] = useState(() => {
    return localStorage.getItem('staygroup_task_alarm_tone') || 'soft';
  });
  const [idleDelaySec, setIdleDelaySec] = useState(() => {
    return Number(localStorage.getItem('staygroup_task_idle_sec') || 20);
  });

  const hotelId = hotel?.id;
  const lastActivityRef = useRef(Date.now());
  const snoozedUntilRef = useRef(0);

  const loadTasks = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const data = await tasksApi.list(hotelId, { status: 'all' });
      setTasks(data || []);
    } catch (err) {
      console.error('Failed to fetch guest tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  useEffect(() => {
    if (hotelId && (!propsRooms || propsRooms.length === 0)) {
      roomsApi.list(hotelId).then(r => setRooms(r || [])).catch(() => {});
    } else if (propsRooms) {
      setRooms(propsRooms);
    }
  }, [hotelId, propsRooms]);

  // Idle detection & sound reminder effect
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);

    const interval = setInterval(() => {
      const now = Date.now();
      const isTyping = document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName);
      const isIdle = (now - lastActivityRef.current) >= (idleDelaySec * 1000);
      const isNotSnoozed = now > snoozedUntilRef.current;

      const pendingList = tasks.filter(t => t.status === 'pending');

      if (isIdle && !isTyping && isNotSnoozed && pendingList.length > 0 && !reminderPopupOpen && !modalOpen && !settingsOpen) {
        setReminderPopupOpen(true);
        if (soundEnabled) {
          playTaskSound(alarmTone);
        }
      }
    }, 3000);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      clearInterval(interval);
    };
  }, [tasks, idleDelaySec, soundEnabled, alarmTone, reminderPopupOpen, modalOpen, settingsOpen]);

  const [editingTaskId, setEditingTaskId] = useState(null);

  const handleOpenEdit = (t) => {
    const isKnownCat = TASK_CATEGORIES.some(c => c.id === t.category);
    setEditingTaskId(t.id);
    setForm({
      room_number: t.room_number || '',
      category: isKnownCat ? t.category : 'Others',
      custom_category: isKnownCat ? '' : (t.category || ''),
      title: t.title || '',
      notes: t.notes || '',
      due_time: t.due_time || '',
      target_shift: t.target_shift || 'Any'
    });
    setModalOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);

    const finalCategory = form.category === 'Others' 
      ? (form.custom_category.trim() || 'Others')
      : form.category;

    try {
      const payload = {
        hotel_id: hotelId,
        room_number: form.room_number || 'Front Desk',
        category: finalCategory,
        title: form.title,
        notes: form.notes,
        due_time: form.due_time,
        target_shift: form.target_shift
      };

      if (editingTaskId) {
        await tasksApi.update(editingTaskId, payload);
      } else {
        await tasksApi.create(payload);
      }

      setForm({
        room_number: '',
        category: 'Wake-up Call',
        custom_category: '',
        title: '',
        notes: '',
        due_time: '',
        target_shift: 'Any'
      });
      setEditingTaskId(null);
      setModalOpen(false);
      loadTasks();
    } catch (err) {
      await notify('Failed to save task: ' + (err.message || 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await tasksApi.updateStatus(id, status);
      loadTasks();
    } catch (err) {
      await notify('Failed to update task');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Task', body: 'Delete this task?', confirmText: 'Delete', danger: true })) return;
    try {
      await tasksApi.delete(id);
      loadTasks();
    } catch (err) {
      await notify('Failed to delete task');
    }
  };

  const handleSnooze = (minutes = 5) => {
    snoozedUntilRef.current = Date.now() + (minutes * 60 * 1000);
    setReminderPopupOpen(false);
  };

  const savePreferences = (enabled, tone, idleSec) => {
    setSoundEnabled(enabled);
    setAlarmTone(tone);
    setIdleDelaySec(idleSec);
    localStorage.setItem('staygroup_task_sound_enabled', String(enabled));
    localStorage.setItem('staygroup_task_alarm_tone', tone);
    localStorage.setItem('staygroup_task_idle_sec', String(idleSec));
    setSettingsOpen(false);
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const visibleTasks = tasks.filter(t => {
    if (filterStatus === 'pending') return t.status === 'pending';
    if (filterStatus === 'completed') return t.status === 'completed';
    return true;
  });

  return (
    <div className="panel" style={{ borderRadius: 20, overflow: 'hidden', marginBottom: 24 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
            📌 Room & Shift Handover Follow-ups
            {pendingTasks.length > 0 && (
              <span style={{ fontSize: 11, background: '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: 999, fontWeight: 800 }}>
                {pendingTasks.length} Pending
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
            Track wake-up calls, parcel deliveries, room requests & shift handovers
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Status Tabs */}
          <div style={{ display: 'flex', background: 'var(--s2)', padding: 3, borderRadius: 10, gap: 2 }}>
            <button 
              className={`btn btn-sm ${filterStatus === 'pending' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterStatus('pending')}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Pending ⏳
            </button>
            <button 
              className={`btn btn-sm ${filterStatus === 'completed' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterStatus('completed')}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Done ✅
            </button>
            <button 
              className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterStatus('all')}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              All
            </button>
          </div>

          <button 
            className="btn btn-sm btn-ghost" 
            onClick={() => setSettingsOpen(true)} 
            title="Alarm & Sound Settings"
            style={{ padding: '6px 10px' }}
          >
            ⚙️ Alarm Settings
          </button>

          <button className="btn btn-sm btn-primary" onClick={() => setModalOpen(true)} style={{ fontWeight: 700 }}>
            ➕ Add Follow-up Task
          </button>
        </div>
      </div>

      {/* Task List */}
      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>Loading tasks...</div>
        ) : visibleTasks.length === 0 ? (
          <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            {filterStatus === 'pending' ? '🎉 No pending tasks! Next shift is clear.' : 'No follow-up tasks found.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {visibleTasks.map(t => {
              const isPending = t.status === 'pending';
              return (
                <div 
                  key={t.id} 
                  style={{
                    background: isPending ? 'var(--s1)' : 'var(--s2)',
                    border: '1px solid var(--border)',
                    borderLeft: `4px solid ${isPending ? '#f59e0b' : '#22c55e'}`,
                    borderRadius: 14,
                    padding: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    opacity: isPending ? 1 : 0.75
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, background: 'rgba(245,158,11,0.12)', color: '#b45309', padding: '3px 8px', borderRadius: 8 }}>
                        {t.category}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 900, background: 'var(--s2)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 6 }}>
                        🚪 Room {t.room_number || 'Desk'}
                      </span>
                    </div>

                    <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
                      {t.title}
                    </div>

                    {t.notes && (
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                        {t.notes}
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                      {t.due_time && <span>⏰ <strong>Due:</strong> {formatTime12(t.due_time)}</span>}
                      {t.target_shift && <span>🔄 <strong>Shift:</strong> {t.target_shift}</span>}
                    </div>
                  </div>

                  <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--muted)' }}>
                    <div>
                      <span>By <strong>{t.created_by_name || 'Staff'}</strong></span>
                      {t.completed_by_name && (
                        <div style={{ color: 'var(--success)' }}>
                          Completed by <strong>{t.completed_by_name}</strong>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 6 }}>
                      {isPending ? (
                        <button 
                          className="btn btn-xs btn-success" 
                          onClick={() => handleUpdateStatus(t.id, 'completed')}
                          style={{ padding: '4px 8px', fontSize: 11, fontWeight: 800 }}
                        >
                          Mark Done ✅
                        </button>
                      ) : (
                        <button 
                          className="btn btn-xs btn-ghost" 
                          onClick={() => handleUpdateStatus(t.id, 'pending')}
                          style={{ padding: '4px 8px', fontSize: 11 }}
                        >
                          Re-open 🔄
                        </button>
                      )}
                      <button 
                        className="btn btn-xs btn-ghost" 
                        onClick={() => handleOpenEdit(t)}
                        style={{ padding: '4px 6px' }}
                        title="Edit task details"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn btn-xs btn-ghost" 
                        onClick={() => handleDelete(t.id)}
                        style={{ padding: '4px 6px', color: 'var(--danger)' }}
                        title="Delete task"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Task Modal */}
      {modalOpen && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <h3 style={{ marginTop: 0 }}>{editingTaskId ? '✏️ Edit Follow-up Task' : '➕ New Guest Task / Shift Follow-up'}</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: -6, marginBottom: 16 }}>
              {editingTaskId ? 'Update task details or instructions' : 'Assign work or instructions for the next shift staff'}
            </p>

            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div className="field">
                  <label style={{ fontSize: 12, fontWeight: 700 }}>Room Number</label>
                  <select 
                    className="input" 
                    value={form.room_number} 
                    onChange={e => setForm({ ...form, room_number: e.target.value })}
                  >
                    <option value="">🏢 Front Desk (General)</option>
                    {rooms.map(r => (
                      <option key={r.id || r.number} value={r.number}>
                        🚪 Room {r.number} ({r.room_type || 'Room'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label style={{ fontSize: 12, fontWeight: 700 }}>Category</label>
                  <select 
                    className="input" 
                    value={form.category} 
                    onChange={e => setForm({ ...form, category: e.target.value })}
                  >
                    {TASK_CATEGORIES.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {form.category === 'Others' && (
                <div className="field" style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 700 }}>Specify Custom Category *</label>
                  <input 
                    type="text" 
                    className="input"
                    required
                    placeholder="e.g. Extra Blanket, Taxi Booking, Key Collect"
                    value={form.custom_category}
                    onChange={e => setForm({ ...form, custom_category: e.target.value })}
                  />
                </div>
              )}

              <div className="field" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 700 }}>Task Title / Instruction *</label>
                <input 
                  type="text" 
                  className="input" 
                  required
                  placeholder="e.g. Wake-up call at 6:00 AM, Collect Amazon parcel, Clean Room"
                  value={form.title} 
                  onChange={e => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div className="field">
                  <label style={{ fontSize: 12, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>⏰ Due Time (Clock Picker)</span>
                    {form.due_time && (
                      <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 800 }}>
                        {formatTime12(form.due_time)}
                      </span>
                    )}
                  </label>
                  <input 
                    type="time" 
                    className="input" 
                    value={form.due_time} 
                    onChange={e => setForm({ ...form, due_time: e.target.value })}
                    style={{ height: 38, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                    <button type="button" className="btn btn-xs btn-ghost" style={{ fontSize: 10, padding: '2px 5px' }} onClick={() => setForm({ ...form, due_time: getOffsetTimeString(30) })}>+30m</button>
                    <button type="button" className="btn btn-xs btn-ghost" style={{ fontSize: 10, padding: '2px 5px' }} onClick={() => setForm({ ...form, due_time: getOffsetTimeString(60) })}>+1h</button>
                    <button type="button" className="btn btn-xs btn-ghost" style={{ fontSize: 10, padding: '2px 5px' }} onClick={() => setForm({ ...form, due_time: '07:00' })}>🌅 7 AM</button>
                    <button type="button" className="btn btn-xs btn-ghost" style={{ fontSize: 10, padding: '2px 5px' }} onClick={() => setForm({ ...form, due_time: '17:00' })}>🌆 5 PM</button>
                    <button type="button" className="btn btn-xs btn-ghost" style={{ fontSize: 10, padding: '2px 5px' }} onClick={() => setForm({ ...form, due_time: '22:00' })}>🌙 10 PM</button>
                  </div>
                </div>

                <div className="field">
                  <label style={{ fontSize: 12, fontWeight: 700 }}>Target Shift</label>
                  <select 
                    className="input" 
                    value={form.target_shift} 
                    onChange={e => setForm({ ...form, target_shift: e.target.value })}
                    style={{ height: 38 }}
                  >
                    <option value="Current Shift">⚡ Current Shift</option>
                    <option value="Any">🔄 Next Available Shift</option>
                    <option value="Morning">🌅 Morning Shift</option>
                    <option value="Evening">🌆 Evening Shift</option>
                    <option value="Night">🌙 Night Shift</option>
                  </select>
                </div>
              </div>

              <div className="field" style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, fontWeight: 700 }}>Additional Instructions / Notes</label>
                <textarea 
                  className="input" 
                  rows={3} 
                  placeholder="Details for the staff member on duty..."
                  value={form.notes} 
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button type="button" className="btn btn-ghost" onClick={() => { setModalOpen(false); setEditingTaskId(null); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || !form.title.trim()}>
                  {saving ? 'Saving...' : editingTaskId ? 'Save Changes' : 'Save Follow-up Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alarm Settings Modal */}
      {settingsOpen && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{ maxWidth: 440 }}>
            <h3 style={{ marginTop: 0 }}>⚙️ Alarm & Reminder Preferences</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: -6, marginBottom: 16 }}>
              Configure task follow-up chime and idle reminder behavior
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>Sound Chime Alert</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Play audio when idle with pending tasks</div>
                </div>
                <input 
                  type="checkbox" 
                  checked={soundEnabled} 
                  onChange={e => setSoundEnabled(e.target.checked)} 
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
              </div>

              <div className="field">
                <label style={{ fontSize: 12, fontWeight: 700 }}>Alarm Sound Tone</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select 
                    className="input" 
                    value={alarmTone} 
                    onChange={e => setAlarmTone(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="soft">🔔 Soft Dual-Tone Chime (Recommended)</option>
                    <option value="bell">🎵 Gentle Harmonic Bell</option>
                    <option value="beep">⏰ Alert Beep</option>
                  </select>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-ghost" 
                    onClick={() => playTaskSound(alarmTone)}
                    title="Test Sound"
                  >
                    🔊 Test
                  </button>
                </div>
              </div>

              <div className="field">
                <label style={{ fontSize: 12, fontWeight: 700 }}>Idle Delay Before Reminder</label>
                <select 
                  className="input" 
                  value={idleDelaySec} 
                  onChange={e => setIdleDelaySec(Number(e.target.value))}
                >
                  <option value={15}>15 Seconds of Inactivity</option>
                  <option value={20}>20 Seconds of Inactivity (Default)</option>
                  <option value={30}>30 Seconds of Inactivity</option>
                  <option value={60}>1 Minute of Inactivity</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setSettingsOpen(false)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={() => savePreferences(soundEnabled, alarmTone, idleDelaySec)}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Idle Reminder Popup Modal */}
      {reminderPopupOpen && pendingTasks.length > 0 && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-content" style={{ maxWidth: 480, borderTop: '5px solid #f59e0b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: '#b45309' }}>
                ⏰ Follow-up Task Reminder!
              </h3>
              <button 
                className="btn btn-xs btn-ghost" 
                onClick={() => playTaskSound(alarmTone)}
                title="Replay Chime"
              >
                🔊 Play Sound
              </button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, marginBottom: 14 }}>
              You have <strong>{pendingTasks.length} pending follow-up task(s)</strong> awaiting action:
            </p>

            <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {pendingTasks.map(pt => (
                <div key={pt.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', padding: 12, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#b45309' }}>
                      🚪 Room {pt.room_number || 'Desk'} · {pt.category}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
                      {pt.title}
                    </div>
                    {pt.due_time && <div style={{ fontSize: 11, color: 'var(--muted)' }}>⏰ Due: {pt.due_time}</div>}
                  </div>
                  <button 
                    className="btn btn-sm btn-success"
                    onClick={() => {
                      handleUpdateStatus(pt.id, 'completed');
                      if (pendingTasks.length <= 1) setReminderPopupOpen(false);
                    }}
                    style={{ whiteSpace: 'nowrap', fontWeight: 800 }}
                  >
                    Mark Done ✅
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => handleSnooze(5)}>
                💤 Snooze 5 Min
              </button>
              <button className="btn btn-secondary" onClick={() => setReminderPopupOpen(false)}>
                Dismiss & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
